/**
 * Theme → CSS custom properties. This is the ONLY place brand values become
 * something components can see; components consume `var(--color-…)` etc.
 *
 * Token families (all emitted on `:root` by the ThemeProvider):
 *   --color-*      mode-resolved semantic colors (change with light/dark)
 *   --palette-*    the raw brand scales (style guide + rare explicit steps)
 *   --font-*       display / body / mono stacks
 *   --text-*       type scale;  --leading-*, --tracking-eyebrow, --eyebrow-transform
 *   --radius-*     sm / md / lg / pill
 *   --space-*      spacing scale
 *   --shadow-*     sm / md
 */
import type { ModeTokens, Theme, ThemeMode } from "./types.js";

export type CssVariables = Record<`--${string}`, string>;

const SEMANTIC_KEYS = ["success", "warning", "danger", "info"] as const;

export function themeToCssVariables(theme: Theme, mode: ThemeMode): CssVariables {
  const m = theme.modes[mode];
  const vars: CssVariables = {
    "--color-bg": m.bg,
    "--color-surface": m.surface,
    "--color-surface-raised": m.surfaceRaised,
    "--color-surface-sunken": m.surfaceSunken,
    "--color-border": m.border,
    "--color-border-strong": m.borderStrong,
    "--color-text": m.text,
    "--color-text-secondary": m.textSecondary,
    "--color-text-muted": m.textMuted,
    "--color-text-faint": m.textFaint,
    "--color-accent": m.accent,
    "--color-accent-hover": m.accentHover,
    "--color-accent-fg": m.accentFg,
    "--color-accent-soft": m.accentSoft,
    "--color-accent-border": m.accentBorder,
    "--color-accent-figure": m.accentFigure,
    "--color-eyebrow": m.eyebrow,
    "--color-link": m.link,
    "--color-link-hover": m.linkHover,
    "--color-focus-ring": m.focusRing,
    "--color-row-hover": m.rowHover,
    "--color-overlay": m.overlay,
    "--shadow-sm": m.shadowSm,
    "--shadow-md": m.shadowMd,
    "--font-display": theme.fonts.display.stack,
    "--font-body": theme.fonts.body.stack,
    "--font-mono": theme.fonts.mono.stack,
    "--text-body": theme.type.body,
    "--text-xs": theme.type.xs,
    "--text-sm": theme.type.sm,
    "--text-md": theme.type.md,
    "--text-lg": theme.type.lg,
    "--text-xl": theme.type.xl,
    "--text-2xl": theme.type["2xl"],
    "--text-3xl": theme.type["3xl"],
    "--text-display": theme.type.display,
    "--leading-body": theme.type.leadingBody,
    "--leading-tight": theme.type.leadingTight,
    "--tracking-eyebrow": theme.type.trackingEyebrow,
    "--eyebrow-transform": theme.type.eyebrowTransform,
    "--radius-sm": theme.radius.sm,
    "--radius-md": theme.radius.md,
    "--radius-lg": theme.radius.lg,
    "--radius-pill": theme.radius.pill,
  };
  for (const key of SEMANTIC_KEYS) {
    const s = m.semantic[key];
    vars[`--color-${key}`] = s.fg;
    vars[`--color-${key}-bg`] = s.bg;
    vars[`--color-${key}-border`] = s.border;
  }
  for (const [step, value] of Object.entries(theme.palette.primary)) {
    vars[`--palette-primary-${step}`] = value;
  }
  for (const [step, value] of Object.entries(theme.palette.neutral)) {
    vars[`--palette-neutral-${step}`] = value;
  }
  for (const [step, value] of Object.entries(theme.spacing)) {
    vars[`--space-${step}`] = value;
  }
  return vars;
}

/** Stable, sorted list of the variable names a theme emits (docs + tests). */
export function cssVariableNames(theme: Theme, mode: ThemeMode = "light"): string[] {
  return Object.keys(themeToCssVariables(theme, mode)).sort();
}

/**
 * Metadata for the style guide: which mode tokens are TEXT colors (checked
 * against the surface they sit on) and what each is for.
 */
export interface ColorTokenDoc {
  key: keyof Omit<ModeTokens, "semantic" | "colorScheme" | "shadowSm" | "shadowMd">;
  cssVar: `--color-${string}`;
  description: string;
  /** For text-like tokens: the mode token they are read against. */
  contrastAgainst?: "surface" | "bg" | "accent";
  /** Large-text usage only (KPI figures, headings) — passes at ≥ 3:1. */
  largeTextOnly?: boolean;
}

export const COLOR_TOKEN_DOCS: readonly ColorTokenDoc[] = [
  { key: "bg", cssVar: "--color-bg", description: "Page background" },
  { key: "surface", cssVar: "--color-surface", description: "Cards, panels, table bodies" },
  { key: "surfaceRaised", cssVar: "--color-surface-raised", description: "Menus, popovers, sheets" },
  { key: "surfaceSunken", cssVar: "--color-surface-sunken", description: "Wells, table headers, code" },
  { key: "border", cssVar: "--color-border", description: "Hairline borders" },
  { key: "borderStrong", cssVar: "--color-border-strong", description: "Inputs, emphasized borders" },
  { key: "text", cssVar: "--color-text", description: "Primary text", contrastAgainst: "surface" },
  { key: "textSecondary", cssVar: "--color-text-secondary", description: "Secondary text", contrastAgainst: "surface" },
  { key: "textMuted", cssVar: "--color-text-muted", description: "Muted text, labels", contrastAgainst: "surface" },
  { key: "textFaint", cssVar: "--color-text-faint", description: "Disabled / decorative (not AA-guaranteed)", contrastAgainst: "surface" },
  { key: "accent", cssVar: "--color-accent", description: "Accent fills, borders, icons" },
  { key: "accentHover", cssVar: "--color-accent-hover", description: "Accent fill hover" },
  { key: "accentFg", cssVar: "--color-accent-fg", description: "Text on accent fills", contrastAgainst: "accent" },
  { key: "accentFigure", cssVar: "--color-accent-figure", description: "Large display figures (≥ 3:1)", contrastAgainst: "surface", largeTextOnly: true },
  { key: "eyebrow", cssVar: "--color-eyebrow", description: "Uppercase letter-spaced labels", contrastAgainst: "surface" },
  { key: "link", cssVar: "--color-link", description: "Text links", contrastAgainst: "surface" },
  { key: "linkHover", cssVar: "--color-link-hover", description: "Text link hover", contrastAgainst: "surface" },
];
